import { create } from 'zustand';

import type { CatalogTriggerRequest } from '../catalog/types/catalog-export.types';
import type {
  ExportConnector,
  ExportFilters,
  ExportLayout,
  ExportStatusResponse,
  ExportTenantContext,
} from '../types/export.types';
import { isTerminalExportStatus } from '../types/export.types';

/**
 * One in-flight (or just-finished) export the app-level manager is tracking.
 * Keyed by `exportId` so the SSE runner + progress toast survive navigation
 * away from the page that triggered the export. Retains `sourceWorkflowId` +
 * `filters` so a FAILED export can be re-triggered with the identical request.
 */
export interface ExportSession {
  exportId: string;
  /** Export's own workflow slug (display / ops deep-link). */
  workflowId: string;
  /** The enrichment run being exported — needed to re-trigger on retry. */
  sourceWorkflowId: string;
  /** Human context shown in the toast (e.g. the experiment / batch name). */
  label: string;
  connector: ExportConnector;
  tenantContext: ExportTenantContext;
  /** Remembered so retry re-sends the same request; undefined = full catalog. */
  filters?: ExportFilters;
  /**
   * The template code this export was triggered with; undefined = the
   * connector's raw default. Retained for the same reason as `filters`: a retry
   * must re-send the identical request, and re-triggering a feed export as raw
   * would hand the user a file that looks right and is not.
   */
  template?: string;
  /**
   * Set when the export ran through a file transformer (integrations), so a retry
   * goes back through integrations with the same pick instead of the standard trigger.
   */
  layout?: ExportLayout;
  /**
   * Set on a full-catalog export (`sourceWorkflowId` is then `catalog:GMC|META`):
   * the exact catalog trigger body, so a retry re-sends it through the catalog route.
   */
  catalogRequest?: CatalogTriggerRequest;
  /** Stable Sonner toast id — the runner updates this toast in place. */
  toastId: string;
  /** Freshest status frame from the SSE stream (null until the first frame). */
  latest: ExportStatusResponse | null;
  startedAt: number;
}

interface ExportState {
  sessions: Record<string, ExportSession>;
}

interface ExportActions {
  /** Register a freshly-triggered export so the app-level manager starts streaming. */
  startExport: (session: ExportSession) => void;
  /** Store the latest status frame for an export. No-op if the session is gone. */
  updateExport: (exportId: string, latest: ExportStatusResponse) => void;
  /** Drop a session once the runner has finalised its toast (terminal state). */
  endExport: (exportId: string) => void;
  /** True while any export for this source run is still tracked — gates the trigger button. */
  hasActiveExport: (sourceWorkflowId: string) => boolean;
}

/**
 * Global export store. Lives outside React (module singleton) so the SSE
 * runner + progress toast survive navigation — the app-level
 * `ExportProgressManager` reads `sessions` and owns the streams. Not persisted:
 * a browser download can't resume across a full reload.
 */
export const useExportStore = create<ExportState & ExportActions>((set, get) => ({
  sessions: {},

  startExport: (session) => {
    set((state) => ({ sessions: { ...state.sessions, [session.exportId]: session } }));
  },

  updateExport: (exportId, latest) => {
    set((state) => {
      const session = state.sessions[exportId];
      if (!session) return state;
      return { sessions: { ...state.sessions, [exportId]: { ...session, latest } } };
    });
  },

  endExport: (exportId) => {
    set((state) => {
      if (!(exportId in state.sessions)) return state;
      const sessions = Object.fromEntries(
        Object.entries(state.sessions).filter(([id]) => id !== exportId),
      );
      return { sessions };
    });
  },

  // Only non-terminal sessions count — a lingering COMPLETED/FAILED toast (kept
  // open so the user can download / retry) must not block a fresh trigger.
  hasActiveExport: (sourceWorkflowId) =>
    Object.values(get().sessions).some(
      (s) =>
        s.sourceWorkflowId === sourceWorkflowId &&
        (s.latest == null || !isTerminalExportStatus(s.latest.status)),
    ),
}));
